// =============================================================================
// features/auth/firstAccess.ts — Lógica PURA da tela de primeiro acesso (F56-S02).
//
// Sem React, sem rede, sem store (só tipos): tudo aqui é testável em vitest puro.
//   - schema do formulário (espelha a política da API; a API é quem decide)
//   - regras e indicador de força da senha nova
//   - mapeamento de cada código de erro do contrato (F56-S01) para texto humano
//   - decisão de redirecionamento do guard (pendente x não pendente x rota pública)
//
// LGPD: este módulo nunca recebe nem devolve senha em log/mensagem. As funções que
// olham a senha (`passwordRules`, `passwordStrength`) só devolvem booleanos/níveis.
// =============================================================================

import {
  FIRST_ACCESS_ERROR_CODES,
  loginResponseSchema,
  PASSWORD_MAX_LENGTH,
  type LoginResponse,
} from '@elemento/shared-schemas';
import { z } from 'zod';

import type { FirstAccessState } from '../../lib/auth-store';
import { validatePersonalEmail } from '../account/personalEmail';

export const FIRST_ACCESS_PATH = '/primeiro-acesso';

/** Limite do servidor: 5 tentativas por 15 minutos, por usuário. */
export const RATE_LIMIT_WINDOW_MS = 15 * 60 * 1000;

// ─── Normalização ─────────────────────────────────────────────────────────────

/** Mesma normalização do servidor (trim + minúsculas) — o que enviamos é o que ele grava. */
export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

// ─── Regras da senha nova (espelham `newPasswordPolicySchema` da API) ─────────

export type PasswordRuleId = 'length' | 'letter' | 'digit' | 'edges';

export interface PasswordRule {
  id: PasswordRuleId;
  /** Texto curto, em português simples, para a lista de regras. */
  label: string;
  ok: boolean;
}

const MIN_LENGTH = 8;

/**
 * Avalia a senha nova contra a política do servidor:
 * 8 a 72 caracteres, ao menos uma letra e um número, sem espaço no início/fim.
 * Senha vazia: nenhuma regra é considerada cumprida (a lista aparece "apagada").
 */
export function passwordRules(password: string): PasswordRule[] {
  const filled = password.length > 0;
  return [
    {
      id: 'length',
      label: `Entre ${MIN_LENGTH} e ${PASSWORD_MAX_LENGTH} caracteres`,
      ok: password.length >= MIN_LENGTH && password.length <= PASSWORD_MAX_LENGTH,
    },
    { id: 'letter', label: 'Pelo menos uma letra', ok: /[a-zA-Z]/.test(password) },
    { id: 'digit', label: 'Pelo menos um número', ok: /[0-9]/.test(password) },
    {
      id: 'edges',
      label: 'Sem espaço no começo ou no fim',
      ok: filled && password === password.trim(),
    },
  ];
}

export function meetsPasswordPolicy(password: string): boolean {
  return passwordRules(password).every((r) => r.ok);
}

export type PasswordStrength = 'empty' | 'insufficient' | 'ok' | 'strong';

/**
 * Indicador de força. O que MANDA é a política: abaixo dela é 'insufficient'
 * (o servidor recusaria). Acima dela, 'strong' premia tamanho e variedade — é só
 * orientação; não vira regra nova (a API não a exige).
 */
export function passwordStrength(password: string): PasswordStrength {
  if (password.length === 0) return 'empty';
  if (!meetsPasswordPolicy(password)) return 'insufficient';
  const variety = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((re) => re.test(password));
  return password.length >= 12 && variety.length >= 3 ? 'strong' : 'ok';
}

export const STRENGTH_LABEL: Record<PasswordStrength, string> = {
  empty: 'Digite a senha nova',
  insufficient: 'Ainda não atende às regras',
  ok: 'Atende às regras',
  strong: 'Senha forte',
};

/** Quantos dos 3 segmentos da barra acendem. */
export const STRENGTH_LEVEL: Record<PasswordStrength, 0 | 1 | 2 | 3> = {
  empty: 0,
  insufficient: 1,
  ok: 2,
  strong: 3,
};

// ─── Schema do formulário ─────────────────────────────────────────────────────

export const firstAccessFormSchema = z
  .object({
    currentPassword: z
      .string()
      .min(1, 'Digite a senha temporária que você recebeu')
      .max(
        PASSWORD_MAX_LENGTH,
        'Essa senha temporária parece longa demais. Confira e digite de novo',
      ),
    email: z
      .string()
      .trim()
      .min(1, 'Digite o seu e-mail')
      .max(255, 'Esse e-mail é longo demais')
      .email('Esse e-mail não parece certo. Confira se tem @ e o final, como .com.br'),
    emailConfirm: z.string().trim().min(1, 'Digite o e-mail de novo para confirmar'),
    newPassword: z
      .string()
      .min(1, 'Crie a sua senha nova')
      .refine((s) => s.length === 0 || s.length >= MIN_LENGTH, 'Use pelo menos 8 caracteres')
      .refine(
        (s) => s.length <= PASSWORD_MAX_LENGTH,
        `Use no máximo ${PASSWORD_MAX_LENGTH} caracteres`,
      )
      .refine((s) => s.length === 0 || /[a-zA-Z]/.test(s), 'Use pelo menos uma letra')
      .refine((s) => s.length === 0 || /[0-9]/.test(s), 'Use pelo menos um número')
      .refine((s) => s === s.trim(), 'A senha não pode começar nem terminar com espaço'),
    newPasswordConfirm: z.string().min(1, 'Digite a senha nova de novo para confirmar'),
    /**
     * E-mail pessoal (F58-S15, D14). Opcional AQUI: enquanto o primeiro acesso está
     * pendente o servidor não diz o papel (GET /api/account/profile responde 403), então a
     * tela não sabe se o perfil exige. Em branco = segue; se o perfil exige, o guard do
     * modal pede em seguida. Preenchido = mesma regra do modal (./account/personalEmail).
     */
    personalEmail: z.string(),
  })
  .superRefine((v, ctx) => {
    if (v.personalEmail.trim().length > 0) {
      const problem = validatePersonalEmail(v.personalEmail);
      if (problem !== null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['personalEmail'], message: problem });
      }
    }
    if (
      v.email.trim().length > 0 &&
      v.emailConfirm.trim().length > 0 &&
      normalizeEmail(v.email) !== normalizeEmail(v.emailConfirm)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['emailConfirm'],
        message: 'Os dois e-mails precisam ser iguais',
      });
    }
    if (v.newPassword.length > 0 && v.newPassword === v.currentPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['newPassword'],
        message: 'A senha nova precisa ser diferente da senha temporária',
      });
    }
    if (
      v.newPasswordConfirm.length > 0 &&
      v.newPassword.length > 0 &&
      v.newPasswordConfirm !== v.newPassword
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['newPasswordConfirm'],
        message: 'As duas senhas novas precisam ser iguais',
      });
    }
  });

export type FirstAccessFormValues = z.infer<typeof firstAccessFormSchema>;

export const FIRST_ACCESS_FORM_DEFAULTS: FirstAccessFormValues = {
  currentPassword: '',
  email: '',
  emailConfirm: '',
  newPassword: '',
  newPasswordConfirm: '',
  personalEmail: '',
};

// ─── Erros do servidor → mensagens humanas ────────────────────────────────────

export type FirstAccessField = 'currentPassword' | 'email' | 'newPassword';

/**
 * O que a tela faz com o erro:
 *   field      → mensagem ligada a um campo (e foco nele)
 *   banner     → aviso no topo do formulário
 *   wait       → 429: aviso de espera + botão travado
 *   already    → já concluiu: vai para "/" (estado vira 'done')
 *   relogin    → sessão perdida: oferece voltar ao login
 */
export type FirstAccessErrorView =
  | { kind: 'field'; field: FirstAccessField; message: string }
  | { kind: 'banner'; message: string }
  | { kind: 'wait'; message: string }
  | { kind: 'already'; message: string }
  | { kind: 'relogin'; message: string };

/** Razões de WEAK_PASSWORD que o servidor devolve na `message` → texto simples. */
const WEAK_REASON_PATTERNS: ReadonlyArray<{ re: RegExp; text: string }> = [
  { re: /pelo menos 8/i, text: 'use pelo menos 8 caracteres' },
  { re: /no máximo/i, text: `use no máximo ${PASSWORD_MAX_LENGTH} caracteres` },
  { re: /uma letra/i, text: 'use pelo menos uma letra' },
  { re: /um dígito|um número/i, text: 'use pelo menos um número' },
  { re: /espaço/i, text: 'tire o espaço do começo ou do fim' },
  { re: /diferente da senha atual/i, text: 'a senha nova precisa ser diferente da temporária' },
];

/** Lê `message` do servidor (separada por ". ") e traduz só o que reconhece. */
export function weakPasswordMessage(serverMessage: string): string {
  const texts = new Set<string>();
  for (const part of serverMessage.split('. ')) {
    const hit = WEAK_REASON_PATTERNS.find((p) => p.re.test(part));
    if (hit) texts.add(hit.text);
  }
  if (texts.size === 0) {
    return 'Essa senha não foi aceita. Escolha outra, seguindo as regras abaixo do campo.';
  }
  const list = [...texts].join('; ');
  return `Essa senha não foi aceita: ${list}.`;
}

interface ErrorLike {
  status: number;
  code: string;
  message: string;
}

/** Aceita o `ApiError` de lib/api.ts sem importá-lo (mantém este módulo livre de rede). */
function asErrorLike(error: unknown): ErrorLike | null {
  if (typeof error !== 'object' || error === null) return null;
  const e = error as { status?: unknown; code?: unknown; message?: unknown };
  if (typeof e.status !== 'number') return null;
  return {
    status: e.status,
    code: typeof e.code === 'string' ? e.code : 'HTTP_ERROR',
    message: typeof e.message === 'string' ? e.message : '',
  };
}

const GENERIC_ERROR =
  'Não conseguimos concluir agora. Tente de novo em instantes. Se continuar, avise quem passou a senha para você.';

/**
 * Traduz o erro de POST /api/auth/first-access (contrato final da F56-S01).
 * Decide pelo `code` estável; o status só entra para o que não tem código (429, 5xx, rede).
 * Nunca ecoa a `message` crua do servidor, exceto as razões conhecidas do WEAK_PASSWORD.
 */
export function mapFirstAccessError(error: unknown): FirstAccessErrorView {
  const e = asErrorLike(error);
  if (e === null) {
    // fetch rejeitado (TypeError): sem rede / servidor inalcançável. Nada foi gravado.
    return {
      kind: 'banner',
      message: 'Sem conexão com o sistema. Confira a internet e tente de novo.',
    };
  }

  if (e.status === 429) {
    return {
      kind: 'wait',
      message:
        'Você tentou muitas vezes seguidas. Por segurança, aguarde cerca de 15 minutos antes de tentar de novo.',
    };
  }

  switch (e.code) {
    case FIRST_ACCESS_ERROR_CODES.INVALID_CURRENT_PASSWORD:
      return {
        kind: 'field',
        field: 'currentPassword',
        message:
          'Essa não é a senha temporária. Digite exatamente como recebeu, com atenção às maiúsculas e a espaços sobrando.',
      };
    case FIRST_ACCESS_ERROR_CODES.WEAK_PASSWORD:
      return { kind: 'field', field: 'newPassword', message: weakPasswordMessage(e.message) };
    case FIRST_ACCESS_ERROR_CODES.EMAIL_ALREADY_IN_USE:
      return {
        kind: 'field',
        field: 'email',
        message: 'Esse e-mail já está em uso por outra conta. Use outro e-mail, só seu.',
      };
    case FIRST_ACCESS_ERROR_CODES.FIRST_ACCESS_NOT_PENDING:
      return {
        kind: 'already',
        message: 'Você já concluiu o primeiro acesso. Vamos levar você ao sistema.',
      };
    case FIRST_ACCESS_ERROR_CODES.CREDENTIALS_CHANGE_REQUIRED:
      return { kind: 'banner', message: GENERIC_ERROR };
    case 'FST_ERR_VALIDATION':
      return {
        kind: 'field',
        field: 'email',
        message: 'Esse e-mail não foi aceito. Confira se está escrito certo.',
      };
    case 'SESSION_EXPIRED':
      return {
        kind: 'relogin',
        message: 'Sua sessão terminou. Entre de novo com a senha temporária.',
      };
    default:
      break;
  }

  if (e.status === 401) {
    return {
      kind: 'relogin',
      message: 'Sua sessão terminou. Entre de novo com a senha temporária.',
    };
  }
  return { kind: 'banner', message: GENERIC_ERROR };
}

/**
 * true se o erro é o bloqueio do servidor para quem ainda deve trocar as credenciais.
 * Usado pelo cache de queries para mandar o usuário à tela mesmo se o front achava que
 * ele estava liberado (ex.: um administrador redefiniu a senha com a sessão aberta).
 */
export function isCredentialsChangeRequiredError(error: unknown): boolean {
  const e = asErrorLike(error);
  return (
    e !== null &&
    e.status === 403 &&
    e.code === FIRST_ACCESS_ERROR_CODES.CREDENTIALS_CHANGE_REQUIRED
  );
}

/**
 * Decide se um erro de qualquer chamada deve marcar o primeiro acesso como pendente.
 * Só vale com sessão: sem usuário autenticado não há o que reabrir.
 */
export function shouldMarkFirstAccessPending(error: unknown, isAuthenticated: boolean): boolean {
  return isAuthenticated && isCredentialsChangeRequiredError(error);
}

// ─── Resposta do POST /api/auth/first-access ──────────────────────────────────

/**
 *   apply  → resposta dentro do contrato e sem troca pendente: aplicar a sessão nova.
 *   logout → o servidor JÁ concluiu (cookies novos de refresh/CSRF valem), mas não dá para
 *            confiar na resposta: usa o token para encerrar a sessão no servidor e pede login.
 *   reload → nem token utilizável há: recarrega "/" para o bootstrap restaurar a sessão nova
 *            de forma coerente, em vez de fingir que encerrou (os cookies continuam válidos).
 * Decisão mais simples e segura: nunca deixar cookie vivo com a tela dizendo "saiu".
 */
export type FirstAccessResponseDecision =
  | { type: 'apply'; session: LoginResponse }
  | { type: 'logout'; accessToken: string }
  | { type: 'reload' };

export function decideFirstAccessResponse(raw: unknown): FirstAccessResponseDecision {
  const parsed = loginResponseSchema.safeParse(raw);
  if (parsed.success && !parsed.data.must_change_credentials) {
    return { type: 'apply', session: parsed.data };
  }
  // Fora do contrato, ou ainda pedindo troca (o servidor não faz isso): tenta só o token.
  const token =
    typeof raw === 'object' && raw !== null
      ? (raw as { access_token?: unknown }).access_token
      : null;
  if (typeof token === 'string' && token.length > 0) return { type: 'logout', accessToken: token };
  return { type: 'reload' };
}

/** O `/me` respondeu sobre o mesmo usuário que está no store? (corrida de logout/troca de conta) */
export function isSameSessionUser(storeUserId: string | null | undefined, meId: string): boolean {
  return storeUserId !== null && storeUserId !== undefined && storeUserId === meId;
}

// ─── Espera após 429 ──────────────────────────────────────────────────────────

/** "15 minutos", "1 minuto", "menos de 1 minuto" — para o aviso de espera. */
export function formatWait(msLeft: number): string {
  if (msLeft <= 0) return 'agora';
  const minutes = Math.ceil(msLeft / 60_000);
  if (minutes <= 1) return 'menos de 1 minuto';
  return `${minutes} minutos`;
}

// ─── Guard: para onde ir? ─────────────────────────────────────────────────────

/** `app` = rotas do sistema; `first-access` = a tela; `public` = /login e afins. */
export type GuardSurface = 'app' | 'first-access' | 'public';

export type GuardDecision =
  | { type: 'splash' } //          restaurando a sessão
  | { type: 'resolve' } //         autenticado, mas falta saber se o primeiro acesso está pendente
  | { type: 'login' } //           sem sessão
  | { type: 'to-first-access' } // pendente fora da tela
  | { type: 'to-home' } //         não pendente dentro da tela
  | { type: 'render' };

export interface GuardInput {
  surface: GuardSurface;
  /** Sessão ainda sendo restaurada pelo refresh-cookie (SessionBootstrap). */
  bootstrapping: boolean;
  isAuthenticated: boolean;
  firstAccess: FirstAccessState;
}

/**
 * Decisão única de roteamento. Falha FECHADA: enquanto o estado é 'unknown' nada do
 * sistema renderiza (e, portanto, nada dispara chamada que o servidor recusaria com 403).
 * É cosmética — a autorização real é do servidor.
 */
export function decideGuard(input: GuardInput): GuardDecision {
  if (input.surface === 'public') return { type: 'render' };
  if (input.bootstrapping) return { type: 'splash' };
  if (!input.isAuthenticated) return { type: 'login' };
  if (input.firstAccess === 'unknown') return { type: 'resolve' };

  if (input.firstAccess === 'pending') {
    return input.surface === 'first-access' ? { type: 'render' } : { type: 'to-first-access' };
  }
  return input.surface === 'first-access' ? { type: 'to-home' } : { type: 'render' };
}
